import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Alert,
} from "@mui/material";
import { useAuth } from "../../common/contexts/AuthProvider";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/RUFLogo.png";

const image_placeholder = (
  <Box
    sx={{
      width: 120,
      height: 120,
      bgcolor: "#fff",
      borderRadius: 3,
      mb: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#888",
      fontWeight: 700,
      fontSize: 18,
      boxShadow: 2,
    }}
  >
    <img
      src={logo}
      alt="Romanian United Foundation Logo"
      style={{ width: "100%", height: "100%", borderRadius: "50%" }}
    />
  </Box>
);

const SignInPage = () => {
  const navigate = useNavigate();
  const { handleSignIn, isAuthenticated, isLoading, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/nationbuilder-signin", { replace: true });
    }
  }, [isAuthenticated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email && password) {
      try {
        await handleSignIn(email, password);
        navigate("/nationbuilder-signin", { replace: true });
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F5F5F5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 0,
          borderRadius: 4,
          minWidth: 900,
          minHeight: "50vh",
          maxWidth: "95vw",
          bgcolor: "#232324",
          display: "flex",
          flexDirection: "row",
          overflow: "hidden",
        }}
      >
        {/* Left column */}
        <Box
          sx={{
            width: 300,
            bgcolor: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
          }}
        >
          {image_placeholder}
          <Typography
            variant="h6"
            sx={{
              color: "#02ACF0",
              fontWeight: 900,
              textAlign: "center",
              letterSpacing: 1.5,
              fontSize: 20,
              mt: 1,
            }}
          >
            ROMANIAN UNITED FOUNDATION
          </Typography>
        </Box>
        {/* Right column */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 6,
            bgcolor: "#02ACF0",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              color: "#fff",
              textAlign: "center",
              letterSpacing: 1,
            }}
          >
            Sign In
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                width: "100%",
                maxWidth: 400,
                bgcolor: "rgba(255, 255, 255, 0.9)",
                color: "#d32f2f",
              }}
            >
              {error}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              maxWidth: 400,
            }}
          >
            <TextField
              placeholder="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "rgba(255, 255, 255, 0.9)",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(0, 0, 0, 0.7)",
                },
              }}
            />
            <TextField
              placeholder="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "rgba(255, 255, 255, 0.9)",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.3)",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(0, 0, 0, 0.7)",
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !email || !password}
              sx={{
                mt: 2,
                minWidth: 180,
                minHeight: 50,
                fontWeight: 700,
                fontSize: "1.3rem",
                borderRadius: 2,
                bgcolor: "#fff",
                color: "#226298",
                boxShadow: "0 4px 20px 0 #00055",
                "&:hover": {
                  transform: "scale(1.05)",
                  transition: "transform 0.2s",
                },
                "&:disabled": {
                  bgcolor: "rgba(255, 255, 255, 0.5)",
                  color: "rgba(34, 98, 152, 0.5)",
                  cursor: "not-allowed",
                },
              }}
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SignInPage;
